import type { TilemapTileset } from '@game/utils/TilesetResolver';
import type { DecorationItem } from '@/contexts/editMode.types';

/**
 * Decoration Palette Loader - Extracts available decorations from tileset data
 * Loads decoration tiles from tilesets and generates preview images for the palette
 * Organizes decorations by category based on tileset names
 */
export class DecorationPaletteLoader {
  private scene: Phaser.Scene;
  private tilesets: TilemapTileset[] = [];

  constructor(scene: Phaser.Scene) {
    this.scene = scene;
  }

  /**
   * Load decorations from tileset data
   * @param tilesets - Array of tileset data from the tilemap
   * @returns Array of decoration items for the palette
   */
  loadDecorationsFromTilesets(tilesets: TilemapTileset[]): DecorationItem[] {
    this.tilesets = tilesets;
    const decorations: DecorationItem[] = [];

    // Process tilesets named "decorations" or starting with "decoration" or "decorations_"
    // This matches patterns like "decorations_chairs", "decorations_furniture", etc.
    const decorationTilesets = tilesets.filter(tileset => {
      const name = tileset.name.toLowerCase();
      return name === 'decorations' ||
             name.startsWith('decoration') ||
             name.startsWith('decorations_') ||
             name.includes('_decorations');
    });


    // Process each decoration tileset
    decorationTilesets.forEach(tileset => {
      const category = this.getCategoryFromTilesetName(tileset.name);
      const tilesetDecorations = this.extractDecorationsFromTileset(tileset, category);
      decorations.push(...tilesetDecorations);
    });

    // Sort decorations by category and name
    decorations.sort((a, b) => {
      if (a.category !== b.category) {
        return a.category.localeCompare(b.category);
      }
      return a.name.localeCompare(b.name);
    });

    return decorations;
  }

  /**
   * Extract decorations from a single tileset
   */
  private extractDecorationsFromTileset(tileset: TilemapTileset, category: string): DecorationItem[] {
    const decorations: DecorationItem[] = [];

    // For decoration tilesets, include all tiles, not just those with specific properties
    // Check if tileset has tiles property (image collection)
    if (tileset.tiles && typeof tileset.tiles === 'object') {

      // Process each tile in the tileset
      // Note: tiles is an array where tile.id may not match the array index
      // (Tiled allows gaps in tile IDs), so we must use tile.id for GID computation.
      Object.values(tileset.tiles).forEach((tileData) => {
        const localId = tileData.id;
        const globalId = tileset.firstgid + localId;

        // Extract objectgroup data if present (collision shapes, snap points, etc.)
        const objectgroupData = tileData.objectgroup || null;

        // For decoration tilesets, include all tiles
        const decoration: DecorationItem = {
          id: `${tileset.name}_${localId}`,
          name: this.getTileName(tileData, tileset.name, localId),
          gid: globalId,
          category: category,
          width: tileData.imagewidth || tileset.tilewidth || 32,
          height: tileData.imageheight || tileset.tileheight || 32,
          previewUrl: this.generatePreviewUrl(tileData, tileset),
          imagePath: tileData.image || tileset.image,
          tilesetName: tileset.name,
          localTileId: localId,
          // Include objectgroup data for collision and snap points
          objectgroup: objectgroupData
        } as DecorationItem;


        decorations.push(decoration);
      });
    } else if (tileset.image && tileset.tilewidth && tileset.tileheight && tileset.columns) {
      // Handle regular tileset images (grid of tiles)
      const tileCount = tileset.tilecount || (tileset.columns * Math.floor((tileset.imageheight || 0) / tileset.tileheight));

      for (let i = 0; i < tileCount; i++) {
        const decoration: DecorationItem = {
          id: `${tileset.name}_${i}`,
          name: `${tileset.name.replace(/_/g, ' ')} ${i + 1}`,
          gid: tileset.firstgid + i,
          category: category,
          width: tileset.tilewidth,
          height: tileset.tileheight,
          previewUrl: undefined, // Will be generated later
          imagePath: tileset.image,
          tilesetName: tileset.name,
          localTileId: i
        };

        decorations.push(decoration);
      }
    }

    return decorations;
  }

  /**
   * Get category name from tileset name
   */
  private getCategoryFromTilesetName(tilesetName: string): string {
    // Remove prefixes like 'decoration_' or 'decorations_'
    let cleanName = tilesetName.toLowerCase();
    cleanName = cleanName.replace(/^decorations?_/, '');

    // Map common tileset names to categories
    if (cleanName.includes('chair')) {
      return 'Chairs';
    }
    if (cleanName.includes('desk')) {
      return 'Desks';
    }
    if (cleanName.includes('rug')) {
      return 'Rugs';
    }
    if (cleanName.includes('tree') || cleanName.includes('plant') || cleanName.includes('nature')) {
      return 'Nature';
    }
    if (cleanName.includes('furniture')) {
      return 'Furniture';
    }
    if (cleanName.includes('light') || cleanName.includes('lamp') || cleanName.includes('torch')) {
      return 'Lighting';
    }
    if (cleanName.includes('decor') || cleanName.includes('ornament') || cleanName.includes('statue')) {
      return 'Decorative';
    }
    if (cleanName.includes('building') || cleanName.includes('structure') || cleanName.includes('house')) {
      return 'Structures';
    }
    if (cleanName.includes('fence') || cleanName.includes('wall') || cleanName.includes('gate')) {
      return 'Barriers';
    }
    if (cleanName.includes('water') || cleanName.includes('fountain') || cleanName.includes('pond')) {
      return 'Water Features';
    }
    if (cleanName.includes('path') || cleanName.includes('road') || cleanName.includes('tile')) {
      return 'Paths';
    }
    if (cleanName.includes('rock') || cleanName.includes('stone') || cleanName.includes('boulder')) {
      return 'Rocks';
    }
    if (cleanName.includes('flower') || cleanName.includes('grass') || cleanName.includes('bush')) {
      return 'Plants';
    }
    if (cleanName.includes('sign') || cleanName.includes('banner') || cleanName.includes('flag')) {
      return 'Signs';
    }

    // Default category based on cleaned tileset name
    // Capitalize first letter of each word
    return cleanName
      .replace(/_/g, ' ')
      .replace(/\b\w/g, char => char.toUpperCase());
  }

  /**
   * Get tile name from tile data
   */
  private getTileName(tileData: any, tilesetName: string, tileId: number): string {
    // Check for name property
    if (tileData.properties) {
      const props = Array.isArray(tileData.properties) ? tileData.properties : [tileData.properties];
      const nameProp = props.find((prop: any) => prop.name === 'name');
      if (nameProp && nameProp.value) {
        return nameProp.value;
      }
    }

    // If the tile has an image path, try to extract a meaningful name from it
    if (tileData.image) {
      // Extract filename without path and extension
      const filename = tileData.image.split('/').pop()?.replace(/\.[^.]+$/, '') || '';
      // Clean up the filename to be more readable
      const cleanName = filename
        .replace(/_/g, ' ')
        .replace(/([a-z])([A-Z])/g, '$1 $2')
        .replace(/\b\w/g, char => char.toUpperCase());

      if (cleanName && cleanName !== '') {
        return cleanName;
      }
    }

    // Generate name from tileset and ID
    const baseName = tilesetName.replace(/_/g, ' ');
    return `${baseName} ${tileId + 1}`;
  }

  /**
   * Generate preview URL for decoration
   */
  private generatePreviewUrl(tileData: any, tileset: TilemapTileset): string | undefined {
    // If tile has individual image, use that
    if (tileData.image) {
      // Convert relative path to absolute URL
      // Remove any ../ prefixes and ensure proper path
      const cleanPath = tileData.image.replace(/\.\.\//g, '').replace(/^\//, '');

      // Check if it already starts with assets/
      if (cleanPath.startsWith('assets/')) {
        return `/${cleanPath}`;
      } else {
        return `/assets/${cleanPath}`;
      }
    }

    // If tileset has image, we'll need to extract the tile from it later
    // This requires canvas manipulation which we'll do in loadAllPreviews
    return undefined;
  }

  /**
   * Generate preview image from tileset sprite
   * This creates a canvas element with the specific tile extracted
   */
  async generatePreviewFromTileset(
    tilesetImage: string,
    tileId: number,
    tileWidth: number,
    tileHeight: number,
    columns: number
  ): Promise<string> {
    return new Promise((resolve) => {
      // Load the tileset image
      const img = new Image();
      img.onload = () => {
        // Calculate tile position in tileset
        const tileX = (tileId % columns) * tileWidth;
        const tileY = Math.floor(tileId / columns) * tileHeight;

        // Create canvas to extract tile
        const canvas = document.createElement('canvas');
        canvas.width = tileWidth;
        canvas.height = tileHeight;
        const ctx = canvas.getContext('2d');

        if (ctx) {
          // Disable smoothing to preserve pixel art crispness
          ctx.imageSmoothingEnabled = false;
          // Draw the specific tile
          ctx.drawImage(
            img,
            tileX, tileY, tileWidth, tileHeight,  // Source
            0, 0, tileWidth, tileHeight            // Destination
          );

          // Convert to data URL
          const dataUrl = canvas.toDataURL('image/png');
          resolve(dataUrl);
        } else {
          resolve('');
        }
      };

      img.onerror = () => {
        console.warn(`[DecorationPaletteLoader] Failed to load tileset image: ${tilesetImage}`);
        resolve('');
      };

      // Ensure proper path for tileset images
      const cleanPath = tilesetImage.replace(/\.\.\//g, '').replace(/^\//, '');
      if (cleanPath.startsWith('assets/')) {
        img.src = `/${cleanPath}`;
      } else {
        img.src = `/assets/${cleanPath}`;
      }
    });
  }

  /**
   * Load all decoration previews asynchronously
   */
  async loadAllPreviews(decorations: DecorationItem[]): Promise<DecorationItem[]> {
    const decorationsWithPreviews = await Promise.all(
      decorations.map(async (decoration) => {
        if (!decoration.previewUrl) {
          // Try to generate preview from tileset
          const tileset = this.findTilesetForGid(decoration.gid);
          if (tileset && tileset.image) {
            const localId = decoration.gid - tileset.firstgid;
            const preview = await this.generatePreviewFromTileset(
              tileset.image,
              localId,
              tileset.tilewidth || 32,
              tileset.tileheight || 32,
              tileset.columns || 1
            );
            return { ...decoration, previewUrl: preview };
          }
        }
        return decoration;
      })
    );

    return decorationsWithPreviews;
  }

  /**
   * Find tileset for a given global ID
   */
  private findTilesetForGid(gid: number): TilemapTileset | null {
    for (let i = this.tilesets.length - 1; i >= 0; i--) {
      const tileset = this.tilesets[i];
      if (gid >= tileset.firstgid) {
        return tileset;
      }
    }
    return null;
  }

  /**
   * Get decorations by category
   */
  getDecorationsByCategory(decorations: DecorationItem[], category: string): DecorationItem[] {
    if (category === 'all') {
      return decorations;
    }
    return decorations.filter(d => d.category === category);
  }

  /**
   * Get all unique categories from decorations
   */
  getCategories(decorations: DecorationItem[]): string[] {
    const categories = new Set<string>();
    categories.add('all'); // Always include 'all' category

    decorations.forEach(d => {
      categories.add(d.category);
    });

    return Array.from(categories).sort();
  }

  /**
   * Search decorations by name
   */
  searchDecorations(decorations: DecorationItem[], searchTerm: string): DecorationItem[] {
    const term = searchTerm.toLowerCase();
    return decorations.filter(d =>
      d.name.toLowerCase().includes(term) ||
      d.category.toLowerCase().includes(term)
    );
  }
}